// The RevenueCat-backed contents of the paywall sheet.
//
// Rendered into ProUpgradeModal's `purchaseSlot` (the Task 6 seam) ONLY when
// purchasing is switched on AND a user is signed in. With no key, or signed
// out, the modal keeps its static "Available soon" plans and this component is
// never mounted, so no RevenueCat bytes are ever fetched for those users.
//
// Rules this component exists to enforce:
//  - The app_user_id is the Supabase user id, always. configureRc throws
//    without one; mounting requires one.
//  - Prices come from the OFFERING when RevenueCat gives them, and fall back to
//    the proCopy strings only when a package omits its formatted price.
//  - A completed charge is NEVER shown as a failure. After 'purchased' the only
//    two outcomes are "activated" and "activation is taking a minute", and the
//    second one is styled as information, not an error.
//  - A cancelled checkout is silent. The user closed a form; there is nothing
//    to report.

import { useCallback, useEffect, useRef, useState } from 'react'
import { legalWithLinks } from './legalWithLinks'
import { useQueryClient } from '@tanstack/react-query'
import { pollProStatusUntilPro, proStatusQueryKey } from '../../api/proStatus'
import { trackEvent } from '../../lib/analytics'
import { useToast } from '../../hooks/useToast'
import { useSubscriptionSummary } from '../../hooks/useSubscriptionSummary'
import {
  configureRc,
  getProOffering,
  purchasePro,
  type RcPackage,
} from '../../lib/revenuecat'
import {
  paywallActivatedToast,
  paywallActivating,
  paywallActivationPending,
  paywallCheckoutLabel,
  paywallCloseLabel,
  paywallLoadingPlans,
  paywallManageFallback,
  GRANT_STORES,
  paywallManageGrant,
  paywallManageLabel,
  paywallManageNoLink,
  paywallManageUnavailable,
  storeCancelCopy,
  paywallPlansUnavailable,
  paywallLegal,
  paywallPurchaseFailed,
  paywallTrialHook,
  paywallSubscribeLabel,
  planAnnualLabel,
  planAnnualNote,
  planMonthlyLabel,
  priceAnnualFallback,
  priceMonthlyFallback,
} from './proCopy'

export interface ProPurchasePanelProps {
  /** Supabase user id. Doubles as the RevenueCat app_user_id. */
  userId: string
  /** Supabase access token, used to poll /pro/status after a purchase. */
  token: string
  /** Already entitled: show the manage row instead of the plans. */
  isPro?: boolean
  /** Closes the paywall sheet (used after a successful activation). */
  onClose: () => void
}

// idle:       plans on screen, nothing in flight
// purchasing: RevenueCat checkout is open
// activating: charge done, polling /pro/status
// pending:    poll timed out; the charge is fine, the entitlement is late
type Phase = 'loading' | 'idle' | 'purchasing' | 'activating' | 'pending'

export default function ProPurchasePanel({ userId, token, isPro = false, onClose }: ProPurchasePanelProps) {
  // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
  const [phase, setPhase] = useState<Phase>('loading')
  const [monthly, setMonthly] = useState<RcPackage | null>(null)
  const [annual, setAnnual] = useState<RcPackage | null>(null)
  // The user id the packages/error on screen were resolved for. Anything other
  // than the current userId means the SDK is mid re-bind and NOTHING here is
  // safe to click (see the offering effect).
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  // Inline failure copy. Only ever set for a failure that is genuinely a
  // failure: a checkout that threw, or an offering we could not read.
  const [error, setError] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Aborts the post-purchase poll when the sheet unmounts, so a closed paywall
  // does not keep hitting /pro/status for 90 seconds.
  const pollAbortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    return () => pollAbortRef.current?.abort()
  }, [])

  // Configure + read the offering. Runs once per mount (the sheet is unmounted
  // when closed), and configureRc itself is idempotent for the same user.
  //
  // MONEY PATH: when userId CHANGES (user A signed out and user B signed in
  // without a reload) this effect re-runs, but until it settles the SDK is
  // still bound to A's app_user_id. A plan button left on screen in that window
  // would charge B's card against A's RevenueCat customer.
  //
  // `loadedFor` is what makes that impossible: it records WHICH user the plans
  // on screen were resolved for, and the render below treats anything else as
  // "still loading". Because it is state compared during render (not a clear
  // performed inside the effect), the buttons are gone in the SAME render as
  // the prop change, before any click can land.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await configureRc(userId)
        const offering = await getProOffering()
        if (cancelled) return
        setMonthly(offering.monthly)
        setAnnual(offering.annual)
        setError(null)
        setLoadedFor(userId)
        // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
        setPhase('idle')
      } catch {
        if (cancelled) return
        // Nothing is purchasable. Say so plainly rather than showing plan
        // buttons that cannot work.
        setMonthly(null)
        setAnnual(null)
        setError(paywallPlansUnavailable())
        setLoadedFor(userId)
        // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
        setPhase('idle')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  const buy = useCallback(
    async (pkg: RcPackage, plan: 'monthly' | 'annual') => {
      setError(null)
      // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
      setPhase('purchasing')
      trackEvent('pro_purchase_started', { plan })

      // Created BEFORE the checkout, not after it. The sheet can be closed
      // while RevenueCat's form is open, and unmount cleanup can only abort a
      // controller that already exists: created later, an orphaned closure
      // would start a fresh 90 second poll (and possibly toast) against a
      // component nobody is looking at any more.
      const controller = new AbortController()
      pollAbortRef.current = controller

      // Belt and braces for the identity race: the effect above already blanks
      // the plans when userId changes, but a click can still be in flight from
      // the frame before that. configureRc is cached-idempotent (same id is a
      // no-op, a different id calls changeUser), so re-awaiting it here costs
      // nothing and GUARANTEES the SDK is bound to THIS userId before any money
      // moves. Nothing has been charged if it throws, so the copy must not
      // hint at a charge.
      try {
        await configureRc(userId)
      } catch {
        if (controller.signal.aborted) return
        trackEvent('pro_purchase_failed', { plan, reason: 'rc_bind_failed' })
        setError(paywallPlansUnavailable())
        // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
        setPhase('idle')
        return
      }
      if (controller.signal.aborted) return

      let outcome: 'purchased' | 'cancelled'
      try {
        outcome = await purchasePro(pkg)
      } catch {
        if (controller.signal.aborted) return
        // A real checkout failure. The copy hedges on whether a charge landed
        // because the client genuinely cannot know.
        trackEvent('pro_purchase_failed', { plan, reason: 'checkout_error' })
        setError(paywallPurchaseFailed())
        // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
        setPhase('idle')
        return
      }

      if (controller.signal.aborted) return

      if (outcome === 'cancelled') {
        // The user closed the form. Silent return to the plans.
        trackEvent('pro_purchase_cancelled', { plan })
        // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
        setPhase('idle')
        return
      }

      // Charged. From here nothing is allowed to look like a failure.
      // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
      setPhase('activating')
      trackEvent('pro_purchase_completed', { plan })
      const status = await pollProStatusUntilPro(token, { signal: controller.signal })
      if (controller.signal.aborted) return

      if (status) {
        void queryClient.invalidateQueries({ queryKey: proStatusQueryKey(userId) })
        toast(paywallActivatedToast())
        trackEvent('pro_activation_confirmed', { plan })
        onClose()
        return
      }

      // Timed out. The entitlement is late, not missing: keep the sheet open
      // with informational copy and let the user dismiss it themselves.
      trackEvent('pro_activation_pending', { plan })
      // eslint-disable-next-line i18next/no-literal-string -- purchase phase code
      setPhase('pending')
    },
    [onClose, queryClient, toast, token, userId],
  )

  if (isPro) return <ManageRow userId={userId} />

  if (phase === 'activating') {
    return (
      <div className="space-y-2" data-testid="pro-purchase-panel">
        <p data-testid="pro-purchase-activating" className="text-sm text-gray-200">
          {paywallActivating()}
        </p>
      </div>
    )
  }

  if (phase === 'pending') {
    return (
      <div className="space-y-3" data-testid="pro-purchase-panel">
        {/* Neutral tone on purpose: the payment worked. */}
        <p data-testid="pro-purchase-pending" className="text-sm text-gray-200">
          {paywallActivationPending()}
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="pro-purchase-close"
          className="w-full px-4 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
        >
          {paywallCloseLabel()}
        </button>
      </div>
    )
  }

  const busy = phase === 'purchasing'
  // Plans belonging to a PREVIOUS user id read as "loading": same treatment as
  // a first load, so nothing purchasable is on screen while the SDK re-binds.
  const boundToUser = loadedFor === userId
  const loading = phase === 'loading' || !boundToUser
  const hasPlans = !!monthly || !!annual
  const trialLength = trialDays(monthly) ?? trialDays(annual)

  return (
    <div className="space-y-2" data-testid="pro-purchase-panel">
      {loading && <p className="text-sm text-gray-400">{paywallLoadingPlans()}</p>}

      {/* The hook. Rendered only when RevenueCat itself reports a free trial
          on a visible plan, with the day count read off that trial phase. */}
      {!loading && hasPlans && trialLength !== null && (
        <p
          data-testid="pro-trial-hook"
          className="text-sm font-medium text-emerald-300"
        >
          {paywallTrialHook(trialLength)}
        </p>
      )}

      {!loading && monthly && (
        <PlanButton
          // eslint-disable-next-line i18next/no-literal-string -- test id
          testId="pro-plan-monthly"
          label={planMonthlyLabel()}
          price={formattedPrice(monthly) ?? priceMonthlyFallback()}
          busy={busy}
          // eslint-disable-next-line i18next/no-literal-string -- plan code (analytics)
          onClick={() => void buy(monthly, 'monthly')}
        />
      )}
      {!loading && annual && (
        <PlanButton
          // eslint-disable-next-line i18next/no-literal-string -- test id
          testId="pro-plan-annual"
          label={planAnnualLabel()}
          price={formattedPrice(annual) ?? priceAnnualFallback()}
          note={planAnnualNote()}
          busy={busy}
          // eslint-disable-next-line i18next/no-literal-string -- plan code (analytics)
          onClick={() => void buy(annual, 'annual')}
        />
      )}

      {!loading && !hasPlans && !error && (
        <p className="text-sm text-gray-400" data-testid="pro-plans-unavailable">
          {paywallPlansUnavailable()}
        </p>
      )}

      {/* An error raised for the previous user is not this user's problem. */}
      {error && boundToUser && (
        <p data-testid="pro-purchase-error" className="text-xs text-red-300">
          {error}
        </p>
      )}

      {/* Renewal terms and the two legal links, on the surface that takes the
          money. Rendered whatever the plan state is: a customer looking at
          "plans unavailable" is still entitled to read what they would be
          agreeing to, and Apple expects the equivalent links on the iOS
          paywall when that ships. Plain anchors opening a NEW TAB rather than
          router Links: the panel is a modal over an in-flight checkout, and
          navigating the SPA away from it would throw that state away.
          ONE catalog sentence (`pro:paywall_legal`) with the two links as
          tags <0> and <1>, so a translator sees the whole agreement and can
          move the links where the language needs them. */}
      <p className="pt-1 text-[11px] leading-snug text-gray-500" data-testid="pro-purchase-legal">
        {legalWithLinks(paywallLegal())}
      </p>
    </div>
  )
}

/**
 * The already-subscribed row.
 *
 * managementURL is fetched lazily (and only for a Pro customer), so nobody
 * pays for a customer-info round trip just by opening the sheet. Absent URL
 * means the subscription is not web-billed from RevenueCat's point of view,
 * which is what the static fallback copy is for.
 *
 * It configures the SDK ITSELF rather than relying on the parent's effect.
 * React runs a child's effects BEFORE its parent's on mount, so this effect
 * starts first; without its own configureRc the getCustomerInfo call would hit
 * an unconfigured SDK (getSharedInstance throws UninitializedPurchasesError),
 * the catch below would swallow it, and a web-billed subscriber would see the
 * App Store fallback on the first paywall open of a session and the real link
 * on every later one. configureRc is idempotent and serialized, so calling it
 * from both places costs nothing.
 */
function ManageRow({ userId }: { userId: string }) {
  const { url, resolved, outcome, store } = useSubscriptionSummary(userId)

  if (!resolved) return null

  // A null URL is not one state. This row used to collapse all of them into
  // "manage it in the iOS app or the App Store", which was false for a comped
  // account, false for a web subscriber whose lookup was blocked, and false
  // about the iOS app, which has no paywall in it. Same branch order as the
  // account page's Subscription card, on purpose: these are the two places a
  // customer is told where to cancel.
  return (
    <div className="space-y-2" data-testid="pro-manage-row">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pro-manage-link"
          className="block w-full text-center px-4 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
        >
          {paywallManageLabel()}
        </a>
      ) : store && GRANT_STORES.has(store) ? (
        <p data-testid="pro-manage-grant" className="text-sm text-gray-400">
          {paywallManageGrant()}
        </p>
      ) : storeCancelCopy(store) ? (
        // A store the server CONFIRMED, with no link we could produce. Same
        // branch and same order as the account card.
        <p data-testid="pro-manage-store" className="text-sm text-gray-400">
          {storeCancelCopy(store)}
        </p>
      ) : store ? (
        <p data-testid="pro-manage-no-link" className="text-sm text-gray-400">
          {paywallManageNoLink()}
        </p>
      ) : outcome === 'error' ? (
        <p data-testid="pro-manage-unavailable" className="text-sm text-gray-400">
          {paywallManageUnavailable()}
        </p>
      ) : (
        <p data-testid="pro-manage-static" className="text-sm text-gray-400">
          {paywallManageFallback()}
        </p>
      )}
    </div>
  )
}

// RevenueCat localizes and formats the price for us; the proCopy fallback only
// applies when the package omits it entirely.
function formattedPrice(pkg: RcPackage): string | null {
  const price = pkg.webBillingProduct?.price?.formattedPrice
  return price ? price : null
}

// Days of free trial on a package, from the product's OWN trial phase, so the
// hook can never promise a trial checkout will not grant. Only units that are
// exact day counts convert; a month-denominated trial (28-31 days) returns
// null and the hook stays hidden rather than naming a wrong number.
function trialDays(pkg: RcPackage | null): number | null {
  const period = pkg?.webBillingProduct?.freeTrialPhase?.period
  if (!period || !(period.number > 0)) return null
  if (period.unit === 'day') return period.number
  if (period.unit === 'week') return period.number * 7
  return null
}

function PlanButton({
  testId,
  label,
  price,
  note,
  busy,
  onClick,
}: {
  testId: string
  label: string
  price: string
  note?: string
  busy: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-purple-500/50 bg-purple-600/15 text-left hover:bg-purple-600/25 transition-colors disabled:opacity-60 disabled:cursor-wait"
    >
      <span className="flex flex-col">
        <span className="text-sm font-medium text-gray-100">
          {label}
          {note && (
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-600/25 border border-purple-500/40 text-purple-200">
              {note}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-300">{price}</span>
      </span>
      <span className="text-xs text-purple-200 shrink-0">
        {busy ? paywallCheckoutLabel() : paywallSubscribeLabel()}
      </span>
    </button>
  )
}
